import React, { useEffect, useState } from "react";
import { Table, Pagination, Spinner } from "react-bootstrap";
import { BsFiletypeXls } from "react-icons/bs";
import { MdFilterAltOff, MdFilterAlt } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import Swal from "sweetalert2";

const API_URL = process.env.REACT_APP_API_URL;

function TDSReport() {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState({ amount: "", type: "" });
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterToType, setFilterToType] = useState("");
  const [filterAllData, setFilterAllData] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);

  const token = localStorage.getItem("token");
  const pageLimit = 10;

  const handleToggle = () => setIsFilterActive(!isFilterActive);

  const showCustomMessageModal = (title, text) => {
    alert(`${title}: ${text}`);
  };

  const formatDateForServer = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  };

  const fetchLedger = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: searchTerm.type || filterToType,
        from_date: formatDateForServer(filterFromDate),
        to_date: formatDateForServer(filterToDate),
        filterAllData: filterAllData,
        page,
      });
      const response = await fetch(
        `${API_URL}/get-tsd-report-for-associate?${params}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await response.json();

      if (result.success === "1") {
        setLedger(result.data || []);
        setTotalPages(result.total_pages || 1);
        setCurrentPage(result.current_page || 1);
      } else {
        showCustomMessageModal(
          "Error",
          result.message || "Failed to fetch records."
        );
      }
    } catch (error) {
      showCustomMessageModal("Error", "Failed to fetch ledger data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLedger(1);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      fetchLedger(pageNumber);
    }
  };

  const getPaginationGroup = () => {
    const start = Math.floor((currentPage - 1) / pageLimit) * pageLimit;
    return new Array(Math.min(pageLimit, totalPages - start))
      .fill()
      .map((_, idx) => start + idx + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatAmount = (amount) => {
    if (!amount) return "0.00";
    return parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleDownloadFilteredExcel = async () => {
    const token = localStorage.getItem("token");

    try {
      Swal.fire({
        title: "Please wait...",
        text: "Generating your filtered Excel file",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const params = new URLSearchParams({
        totalPages,
        amount: searchTerm.amount,
        type: searchTerm.type || filterToType,
        from_date: formatDateForServer(filterFromDate),
        to_date: formatDateForServer(filterToDate),
      }).toString();

      const response = await fetch(
        `${API_URL}/download-property-income-excel-by-associate?${params}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to download Excel");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Filtered Users Wallet Ledger Report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: "success",
        title: "Download Successful!",
        text: "Filtered Excel report has been downloaded successfully.",
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (error) {
      console.error("Filtered Excel download failed:", error);
      Swal.fire({
        icon: "warning",
        title: "Download Failed!",
        text: "Please Check Entered From Date & To Date For Downloading TDS Report.",
      });
    }
  };

  // ═══════════ INLINE STYLES ═══════════
  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  const styles = {
    filterToggle: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "42px",
      height: "42px",
      padding: 0,
      border: isFilterActive
        ? "1px solid #ffffff"
        : "1px solid rgba(255, 255, 255, 0.3)",
      background: isFilterActive ? "#ffffff" : "rgba(255, 255, 255, 0.15)",
      color: isFilterActive ? "#1e1b4b" : "#ffffff",
      borderRadius: "10px",
      fontSize: "18px",
      cursor: "pointer",
      transition: "all 0.25s ease",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      backdropFilter: "blur(6px)",
    },
    downloadBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      height: "42px",
      padding: "0 20px",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
      letterSpacing: "0.02em",
    },
    filterInput: {
      flex: "1 1 0",
      minWidth: 0,
      height: "44px",
      padding: "0 14px",
      fontSize: "13.5px",
      border: "1.5px solid #e2e8f0",
      borderRadius: "10px",
      background: "#fff",
      color: "#0f172a",
      outline: "none",
      boxSizing: "border-box",
      margin: 0,
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    },
    searchBtn: {
      flexShrink: 0,
      height: "44px",
      padding: "0 24px",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
      margin: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
    },
  };

  return (
    <div className="padding_15">
      <div className="userlist">
        <div className="card shadow-sm">
          <div className="card-header d-flex flex-wrap-mobile justify-content-between align-items-md-center gap-2">
            <div className="titlepage">
              <h3 className="mb-0">TDS Deductions Report</h3>
            </div>
            <div className="d-flex align-items-center gap-2">
              {/* ═══════════ DOWNLOAD BUTTON ═══════════ */}
              <button
                onClick={handleDownloadFilteredExcel}
                style={styles.downloadBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(76, 29, 149, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 14px rgba(76, 29, 149, 0.35)";
                }}
              >
                <BsFiletypeXls style={{ fontSize: "16px" }} />
                Download Excel
              </button>

              {/* ═══════════ FILTER TOGGLE (mobile + desktop) ═══════════ */}
              <button
                onClick={handleToggle}
                title={isFilterActive ? "Hide Filters" : "Show Filters"}
                style={styles.filterToggle}
                onMouseEnter={(e) => {
                  if (!isFilterActive) {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.25)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFilterActive) {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.15)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }
                }}
              >
                {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
              </button>
            </div>
          </div>

          {/* ═══════════ FILTER SECTION (toggled) ═══════════ */}
          {isFilterActive && (
            <div className="card-body pb-0">
              <form
                onSubmit={handleSearch}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  alignItems: "center",
                  marginBottom: "8px",
                  width: "100%",
                }}
              >
                <input
                  type="date"
                  value={filterFromDate}
                  onChange={(e) => setFilterFromDate(e.target.value)}
                  placeholder="From Date"
                  style={styles.filterInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "#6366f1";
                    e.currentTarget.style.boxShadow =
                      "0 0 0 4px rgba(99, 102, 241, 0.12)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />

                <input
                  type="date"
                  value={filterToDate}
                  onChange={(e) => setFilterToDate(e.target.value)}
                  placeholder="To Date"
                  style={styles.filterInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "#6366f1";
                    e.currentTarget.style.boxShadow =
                      "0 0 0 4px rgba(99, 102, 241, 0.12)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />

                <input
                  type="text"
                  placeholder="Search..."
                  value={filterAllData}
                  onChange={(e) => setFilterAllData(e.target.value)}
                  style={styles.filterInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "#6366f1";
                    e.currentTarget.style.boxShadow =
                      "0 0 0 4px rgba(99, 102, 241, 0.12)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />

                <button
                  type="submit"
                  style={styles.searchBtn}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px rgba(76, 29, 149, 0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 14px rgba(76, 29, 149, 0.35)";
                  }}
                >
                  <FaSearch style={{ fontSize: "13px" }} />
                  Search
                </button>
              </form>
            </div>
          )}

          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading records...</p>
              </div>
            ) : ledger.length === 0 ? (
              <p className="text-center">No records found.</p>
            ) : (
              <>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Approve Date</th>
                      <th>Reference Number</th>
                      <th>Approve Amount</th>
                      <th>TDS %</th>
                      <th>TDS Amount</th>
                      <th>Net Amount (After TDS)</th>
                      <th>Advance Payment</th>
                      <th>Advance Payment Settled</th>
                      <th>Remaining Advance Payment</th>
                      <th>Net Payable Amount</th>
                      <th>Transferred Bank</th>
                      <th>Account Number</th>
                      <th>IFSC Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(currentPage - 1) * pageLimit + index + 1}</td>
                        <td>{formatDate(item.date)}</td>
                        <td>{item.transaction_id}</td>
                        <td>₹ {item.transaction_amount || "0.00"}</td>
                        <td>{item.tds_percent || "0.00"}</td>
                        <td>₹ {item.tds_amount || "0.00"}</td>
                        <td>₹ {item.amount_after_tds || "0.00"}</td>
                        <td>₹ {item.advance_payment || "0.00"}</td>
                        <td>₹ {item.advance_payment_used || "0.00"}</td>
                        <td>
                          ₹{" "}
                          {item.advance_payment - item.advance_payment_used ||
                            "0.00"}
                        </td>
                        <td>₹ {item.net_payment || "0.00"}</td>
                        <td>{item.bank || "-"}</td>
                        <td>{item.account_number || "-"}</td>
                        <td>{item.ifsc_code || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <div className="d-flex justify-content-end mt-3">
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {getPaginationGroup().map((item, index) => (
                      <Pagination.Item
                        key={index}
                        active={item === currentPage}
                        onClick={() => handlePageChange(item)}
                      >
                        {item}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TDSReport;